import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, TabsContent, TabsList, TabsTrigger, Textarea } from '../../components/ui'
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Calendar,
  AlertTriangle,
  TrendingUp,
  Eye,
  Download,
  Settings
} from 'lucide-react'
import DocumentReviewPanel from '../../components/admin/DocumentReviewPanel'
import BookingApproval from '../../components/admin/BookingApproval'
import CaseDocumentPreviewModal from '../../components/admin/CaseDocumentPreviewModal'
import { useAdminPendingDocuments, useAdminAllDocuments } from '../../hooks/useDocuments'
import { useDashboardStats, useCases } from '../../hooks/useCases'
import { useBookingStats } from '../../hooks/useBookings'
import { bookingsService } from '../../services/bookings.service'
import { Document } from '../../types'

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState('pending')
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [showDocumentPreview, setShowDocumentPreview] = useState(false)
  const { data: pendingDocuments, isLoading: pendingLoading, error: pendingError } = useAdminPendingDocuments()
  const { data: allDocuments, isLoading: allLoading, error: allError } = useAdminAllDocuments()
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats()
  const { data: bookingStats } = useBookingStats()
  const { data: allCases } = useCases(1, 1000) // Fetch all cases to get accurate count

  // Fetch pending bookings count on component mount
  React.useEffect(() => {
    const fetchPendingBookingsCount = async () => {
      try {
        const response = await bookingsService.getPendingBookings(1, 1) // Fetch first page to get total count
        setPendingBookingsCount(response.total || 0)
      } catch (error) {
        console.error('Failed to fetch pending bookings count:', error)
        setPendingBookingsCount(0)
      }
    }

    fetchPendingBookingsCount()
  }, [])

  // Real statistics from API
  const stats = {
    totalPending: pendingDocuments?.length || 0,
    totalVerified: allDocuments?.filter(d => d.status === 'VERIFIED').length || 0,
    totalRejected: allDocuments?.filter(d => d.status === 'REJECTED').length || 0,
    totalCases: dashboardStats?.totalCases || allCases?.data?.length || 0
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800'
      case 'UPLOADED':
        return 'bg-blue-100 text-blue-800'
      case 'VERIFIED':
        return 'bg-green-100 text-green-800'
      case 'REJECTED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4" />
      case 'UPLOADED':
        return <FileText className="h-4 w-4" />
      case 'VERIFIED':
        return <CheckCircle className="h-4 w-4" />
      case 'REJECTED':
        return <XCircle className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  const handleReviewComplete = () => {
    // Refetch data when a document review is completed
    window.location.reload()
  }

  const handleBookingActionsComplete = () => {
    // Refetch pending bookings count when booking actions are completed
    const fetchPendingBookingsCount = async () => {
      try {
        const response = await bookingsService.getPendingBookings(1, 1) // Fetch first page to get total count
        setPendingBookingsCount(response.total || 0)
      } catch (error) {
        console.error('Failed to fetch pending bookings count:', error)
        setPendingBookingsCount(0)
      }
    }
    fetchPendingBookingsCount()
  }

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document)
    setShowDocumentPreview(true)
  }

  const handleDownloadDocument = async (document: Document) => {
    try {
      const response = await fetch(`/api/documents/${document.id}/download`)
      if (!response.ok) {
        throw new Error('Failed to download document')
      }
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = window.document.createElement('a')
      a.href = url
      a.download = document.originalName || document.fileName
      window.document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      window.document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download document:', error)
    }
  }

  const handleCloseDocumentPreview = () => {
    setShowDocumentPreview(false)
    setSelectedDocument(null)
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold text-neutral-900">Admin Management</span>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                Admin Access
              </Badge>
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-semibold text-sm">AD</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">
            Admin Management Dashboard
          </h1>
          <p className="text-neutral-600">
            Manage document reviews, booking approvals, and system administration.
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Total Cases</p>
                  <p className="text-2xl font-bold text-neutral-900">{stats.totalCases}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Pending Bookings</p>
                  <p className="text-2xl font-bold text-neutral-900">{pendingBookingsCount}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Pending Documents</p>
                  <p className="text-2xl font-bold text-neutral-900">{stats.totalPending}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-600">Verified Documents</p>
                  <p className="text-2xl font-bold text-neutral-900">{stats.totalVerified}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Management Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="bookings" className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Booking Approvals ({pendingBookingsCount})</span>
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>Pending Documents ({stats.totalPending})</span>
            </TabsTrigger>
            <TabsTrigger value="all" className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>All Documents ({allDocuments?.length || 0})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-6">
            {pendingLoading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-neutral-600">Loading pending documents...</p>
                </CardContent>
              </Card>
            ) : pendingError ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="text-red-600">Error loading pending documents</p>
                </CardContent>
              </Card>
            ) : pendingDocuments && pendingDocuments.length > 0 ? (
              <div className="space-y-6">
                {pendingDocuments.map((document: Document) => (
                  <DocumentReviewPanel
                    key={document.id}
                    document={document}
                    onReviewComplete={handleReviewComplete}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    All Caught Up!
                  </h3>
                  <p className="text-neutral-600">
                    No documents are currently pending review.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="space-y-6">
            <BookingApproval onActionsComplete={handleBookingActionsComplete} />
          </TabsContent>

          <TabsContent value="all" className="space-y-6">
            {allLoading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2 text-neutral-600">Loading all documents...</p>
                </CardContent>
              </Card>
            ) : allError ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="text-red-600">Error loading documents</p>
                </CardContent>
              </Card>
            ) : allDocuments && allDocuments.length > 0 ? (
              <div className="space-y-4">
                {allDocuments.map((document: Document) => (
                  <Card key={document.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{document.originalName}</CardTitle>
                          <div className="flex items-center space-x-2 mt-2">
                            <Badge className={getStatusColor(document.status)}>
                              <div className="flex items-center space-x-1">
                                {getStatusIcon(document.status)}
                                <span>{document.status.replace('_', ' ')}</span>
                              </div>
                            </Badge>
                            <span className="text-sm text-neutral-500">
                              {document.type.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDocument(document)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadDocument(document)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-neutral-500">Case:</span>
                          <span className="ml-2 font-medium">
                            {document.case?.caseNumber || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">Family:</span>
                          <span className="ml-2 font-medium">
                            {document.case?.family?.firstName} {document.case?.family?.lastName}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">Uploaded:</span>
                          <span className="ml-2 font-medium">
                            {new Date(document.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-neutral-500">File Size:</span>
                          <span className="ml-2 font-medium">
                            {(document.fileSize / 1024).toFixed(1)} KB
                          </span>
                        </div>
                      </div>
                      {document.verificationNotes && (
                        <div className="mt-4 p-3 bg-neutral-50 rounded-lg">
                          <p className="text-sm font-medium text-neutral-900 mb-1">Review Notes:</p>
                          <p className="text-sm text-neutral-700">{document.verificationNotes}</p>
                        </div>
                      )}
                      {(document.status === 'PENDING' || document.status === 'UPLOADED') && (
                        <div className="mt-4">
                          <DocumentReviewPanel
                            document={document}
                            onReviewComplete={handleReviewComplete}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <FileText className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    No Documents Found
                  </h3>
                  <p className="text-neutral-600">
                    No documents have been uploaded yet.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Document Preview Modal */}
        <CaseDocumentPreviewModal
          document={selectedDocument}
          isOpen={showDocumentPreview}
          onClose={handleCloseDocumentPreview}
        />
      </div>
    </div>
  )
}